import { useEffect, useState } from 'react';
import { api, ApiError } from '../lib/api';

interface MysteryItem { key: string; title: string }
interface MysteryGroup { type: string; label: string; color: string; items: MysteryItem[] }

const MYSTERIES: MysteryGroup[] = [
  { type: 'gozosos', label: 'Misterios Gozosos', color: '#059669', items: [
    { key: 'gozosos_1', title: 'La Anunciación del Ángel a María' },
    { key: 'gozosos_2', title: 'La Visitación de María a Isabel' },
    { key: 'gozosos_3', title: 'El Nacimiento de Jesús en Belén' },
    { key: 'gozosos_4', title: 'La Presentación de Jesús en el Templo' },
    { key: 'gozosos_5', title: 'El Niño Jesús perdido en el Templo' },
  ]},
  { type: 'luminosos', label: 'Misterios Luminosos', color: '#D97706', items: [
    { key: 'luminosos_1', title: 'El Bautismo de Jesús en el Jordán' },
    { key: 'luminosos_2', title: 'Las Bodas de Caná' },
    { key: 'luminosos_3', title: 'El Anuncio del Reino de Dios' },
    { key: 'luminosos_4', title: 'La Transfiguración del Señor' },
    { key: 'luminosos_5', title: 'La Institución de la Eucaristía' },
  ]},
  { type: 'dolorosos', label: 'Misterios Dolorosos', color: '#DC2626', items: [
    { key: 'dolorosos_1', title: 'La Oración de Jesús en el Huerto' },
    { key: 'dolorosos_2', title: 'La Flagelación del Señor' },
    { key: 'dolorosos_3', title: 'La Coronación de espinas' },
    { key: 'dolorosos_4', title: 'Jesús con la Cruz a cuestas' },
    { key: 'dolorosos_5', title: 'La Crucifixión y muerte de Jesús' },
  ]},
  { type: 'gloriosos', label: 'Misterios Gloriosos', color: '#7C3AED', items: [
    { key: 'gloriosos_1', title: 'La Resurrección de Jesús' },
    { key: 'gloriosos_2', title: 'La Ascensión del Señor' },
    { key: 'gloriosos_3', title: 'La Venida del Espíritu Santo' },
    { key: 'gloriosos_4', title: 'La Asunción de María' },
    { key: 'gloriosos_5', title: 'La Coronación de la Virgen María' },
  ]},
];

const BASE_PRAYERS: { key: string; label: string }[] = [
  { key: 'padre_nuestro', label: 'Padre Nuestro' },
  { key: 'ave_maria', label: 'Ave María' },
  { key: 'gloria', label: 'Gloria' },
];

export default function RosarioPage() {
  const [images, setImages] = useState<Record<string, string>>({});
  const [audios, setAudios] = useState<Record<string, string>>({});
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});
  const [audioUrls, setAudioUrls] = useState<Record<string, string>>({});
  const [savingImage, setSavingImage] = useState<Record<string, boolean>>({});
  const [savingAudio, setSavingAudio] = useState<Record<string, boolean>>({});
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);

  useEffect(() => {
    (async () => {
      try {
        const imgs = await api.get<Record<string, string>>('/rosary-images');
        if (imgs && typeof imgs === 'object') {
          setImages(imgs);
          setImageUrls(prev => ({ ...imgs, ...prev }));
        }
      } catch { /* silent */ }
      try {
        const auds = await api.get<Record<string, string>>('/rosary-audio');
        if (auds && typeof auds === 'object') {
          setAudios(auds);
          setAudioUrls(prev => ({ ...auds, ...prev }));
        }
      } catch { /* silent */ }
    })();
  }, []);

  const saveImage = async (key: string) => {
    const url = (imageUrls[key] || '').trim();
    if (!url) {
      setFeedback({ kind: 'error', text: 'Introduce una URL de imagen' });
      return;
    }
    setSavingImage(prev => ({ ...prev, [key]: true }));
    setFeedback(null);
    try {
      await api.put('/admin/rosary-images', { key, image_url: url });
      setImages(prev => ({ ...prev, [key]: url }));
      setFeedback({ kind: 'success', text: `Imagen guardada (${key})` });
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo guardar la imagen' });
    } finally {
      setSavingImage(prev => ({ ...prev, [key]: false }));
    }
  };

  const saveAudio = async (key: string) => {
    const url = (audioUrls[key] || '').trim();
    if (!url) {
      setFeedback({ kind: 'error', text: 'Introduce una URL de audio' });
      return;
    }
    setSavingAudio(prev => ({ ...prev, [key]: true }));
    setFeedback(null);
    try {
      await api.put('/admin/rosary-audio', { key, audio_url: url });
      setAudios(prev => ({ ...prev, [key]: url }));
      setFeedback({ kind: 'success', text: `Audio guardado (${key})` });
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo guardar el audio' });
    } finally {
      setSavingAudio(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <>
      <h1 className="page-title">Rosario</h1>
      {feedback && (
        <div className={'alert ' + (feedback.kind === 'error' ? 'alert-error' : 'alert-success')}>
          {feedback.text}
        </div>
      )}

      <div className="card">
        <h2 className="section-title">Foto de la Virgen · Portada del Rosario</h2>
        <p className="muted" style={{ marginBottom: 14 }}>
          Imagen que aparece en la parte superior de la pantalla del Santo Rosario,
          bajo el título. Pega una URL de imagen (Firebase Storage, S3…).
        </p>
        <div className="rosario-mystery">
          <div className="rosario-preview">
            {images['portada']
              ? <img src={images['portada']} alt="Foto de la Virgen" />
              : <span>Sin imagen</span>}
          </div>
          <div>
            <div className="field" style={{ marginBottom: 8 }}>
              <label style={{ fontSize: 12 }}>URL imagen</label>
              <input
                type="url"
                value={imageUrls['portada'] || ''}
                onChange={e => setImageUrls(prev => ({ ...prev, portada: e.target.value }))}
                placeholder="https://… .jpg"
              />
            </div>
            <button
              className="btn"
              onClick={() => saveImage('portada')}
              disabled={savingImage['portada']}
            >
              {savingImage['portada'] ? 'Guardando…' : 'Guardar imagen'}
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <h2 className="section-title">Audio · Oraciones base</h2>
        <p className="muted" style={{ marginBottom: 14 }}>
          URLs de audio (Firebase Storage, S3…) que se reproducirán en el Rosario guiado.
        </p>
        {BASE_PRAYERS.map(p => (
          <div key={p.key} style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginTop: 14 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <strong>{p.label}</strong>
              {audios[p.key] && <span className="muted" style={{ fontSize: 12 }}>✓ configurado</span>}
            </div>
            <input
              type="url"
              value={audioUrls[p.key] || ''}
              onChange={e => setAudioUrls(prev => ({ ...prev, [p.key]: e.target.value }))}
              placeholder="https://… .mp3"
              style={{ width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg)' }}
            />
            <button
              className="btn"
              style={{ marginTop: 8 }}
              onClick={() => saveAudio(p.key)}
              disabled={savingAudio[p.key]}
            >
              {savingAudio[p.key] ? 'Guardando…' : 'Guardar audio'}
            </button>
          </div>
        ))}
      </div>

      <h2 className="section-title" style={{ marginTop: 24 }}>Misterios · Imágenes y audio</h2>
      <p className="muted" style={{ marginBottom: 16 }}>
        Pega una URL de imagen (proporción 9:16 recomendada) y de audio para cada misterio.
      </p>
      {MYSTERIES.map(group => (
        <div key={group.type} className="rosario-group">
          <div className="rosario-group-header" style={{ background: group.color }}>
            {group.label}
          </div>
          {group.items.map((m, idx) => (
            <div key={m.key} className="rosario-mystery">
              <div className="rosario-preview">
                {images[m.key]
                  ? <img src={images[m.key]} alt={m.title} />
                  : <span>Sin imagen</span>}
              </div>
              <div>
                <div className="muted-tag muted" style={{ color: group.color }}>Misterio {idx + 1}</div>
                <h4>{m.title}</h4>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12 }}>URL imagen</label>
                  <input
                    type="url"
                    value={imageUrls[m.key] || ''}
                    onChange={e => setImageUrls(prev => ({ ...prev, [m.key]: e.target.value }))}
                    placeholder="https://… .jpg"
                  />
                </div>
                <div className="field" style={{ marginBottom: 8 }}>
                  <label style={{ fontSize: 12 }}>URL audio {audios[m.key] ? '✓' : ''}</label>
                  <input
                    type="url"
                    value={audioUrls[m.key] || ''}
                    onChange={e => setAudioUrls(prev => ({ ...prev, [m.key]: e.target.value }))}
                    placeholder="https://… .mp3"
                  />
                </div>
                <div className="btn-row">
                  <button
                    className="btn"
                    style={{ background: group.color }}
                    onClick={() => saveImage(m.key)}
                    disabled={savingImage[m.key]}
                  >
                    {savingImage[m.key] ? 'Guardando…' : 'Guardar imagen'}
                  </button>
                  <button
                    className="btn-secondary btn"
                    onClick={() => saveAudio(m.key)}
                    disabled={savingAudio[m.key]}
                  >
                    {savingAudio[m.key] ? 'Guardando…' : 'Guardar audio'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}
    </>
  );
}
